<script setup lang="ts">
import type { PriceFetchKind } from '@/composables/jobEdit/useJobEditServicePrice';
import type * as openapi from '@/helpers/api/types/openapiOverrides';
import type { JobEditServiceItem } from '@/models/jobEdit';

import KNumberDecimalField from '@/components/fields/KNumberDecimalField.vue';
import KNumberField from '@/components/fields/KNumberField.vue';
import ServiceCodeAutocompleteField from '@/components/fields/ServiceCodeAutocompleteField.vue';
import { useJobEditServicePrice } from '@/composables/jobEdit/useJobEditServicePrice';
import { createEmptyServiceItem } from '@/models/jobEdit';

export interface Props {
  readonly?: boolean;
  jobId?: number;
  jobDate?: string;
  legNumber?: number;
}

const props = withDefaults(defineProps<Props>(), {
  readonly: false,
  jobId: undefined,
  jobDate: undefined,
  legNumber: undefined,
});

const emit = defineEmits<{
  delete: [event: Event];
}>();

const model = defineModel<JobEditServiceItem>({
  default: () => createEmptyServiceItem(),
});

const {
  isLoading: isPriceLoading,
  fetchPrice,
  debouncedFetchPrice,
} = useJobEditServicePrice({
  jobId: () => props.jobId,
  jobDate: () => props.jobDate,
  legNumber: () => props.legNumber,
  serviceCode: () => model.value.service_code,
});

function applyPrice(
  priceData: openapi.GetJobPriceSc_200,
  kind: PriceFetchKind,
): void {
  const fee = parseFloat(priceData.fee);
  const isScheduled = kind === 'quantity';
  model.value = {
    ...model.value,
    // Preserve scheduled_fee as a baseline reference when user types a
    // custom_fee — it shows what the fee would be at backend pricing.
    scheduled_fee: isScheduled ? fee : model.value.scheduled_fee,
    fee,
    gst: parseFloat(priceData.gst),
    pay: parseFloat(priceData.pay),
    second_quantity: priceData.second_quantity,
  };
}

async function handleServiceCodeUpdate(value: string | null): Promise<void> {
  const serviceCode = value ?? undefined;
  model.value = {
    ...model.value,
    service_code: serviceCode,
    name: serviceCode ?? '',
    quantity: 1,
  };
  if (!serviceCode) return;
  const priceData = await fetchPrice('quantity', 1);
  if (priceData) applyPrice(priceData, 'quantity');
}

async function handleQuantityUpdate(
  value: number | null | undefined,
): Promise<void> {
  const quantity = value ?? 1;
  model.value = { ...model.value, quantity };
  const priceData = await debouncedFetchPrice('quantity', quantity);
  if (priceData) applyPrice(priceData, 'quantity');
}

async function handleFeeUpdate(
  value: number | null | undefined,
): Promise<void> {
  const fee = value ?? 0;
  model.value = { ...model.value, fee };
  if (fee <= 0) return;
  const priceData = await debouncedFetchPrice('custom_fee', fee);
  if (priceData) applyPrice(priceData, 'custom_fee');
}

// The value persists in the model until the next quantity/fee fetch, which
// will overwrite it with backend-computed pay.
function handlePayUpdate(value: number | null | undefined): void {
  model.value = { ...model.value, pay: value ?? 0 };
}
</script>

<template>
  <fieldset
    :class="$style.jobEditServiceFieldset"
    data-testid="jobEditServiceFieldset"
  >
    <ServiceCodeAutocompleteField
      :model-value="model.service_code"
      :readonly="props.readonly"
      :disabled="!props.readonly && isPriceLoading"
      size="sm"
      @update:model-value="handleServiceCodeUpdate"
    />
    <KNumberField
      :model-value="model.quantity"
      label="Quantity (fee)"
      :min="1"
      :readonly="props.readonly"
      :disabled="!props.readonly && isPriceLoading"
      size="sm"
      @update:model-value="handleQuantityUpdate"
    />
    <KNumberDecimalField
      :model-value="model.scheduled_fee"
      label="Sch. (fee)"
      readonly
      :disabled="isPriceLoading"
      size="sm"
    />
    <KNumberDecimalField
      :model-value="model.fee"
      label="Fee"
      :readonly="props.readonly"
      :disabled="!props.readonly && isPriceLoading"
      size="sm"
      @update:model-value="handleFeeUpdate"
    />
    <KNumberDecimalField
      :model-value="model.gst"
      label="GST"
      readonly
      :disabled="isPriceLoading"
      size="sm"
    />
    <KNumberField
      :model-value="model.second_quantity"
      label="Quantity (pay)"
      readonly
      :disabled="isPriceLoading"
      size="sm"
    />
    <KNumberDecimalField
      :model-value="model.pay"
      label="Pay"
      :readonly="props.readonly"
      :disabled="!props.readonly && isPriceLoading"
      size="sm"
      @update:model-value="handlePayUpdate"
    />
    <div :class="$style.actions">
      <v-progress-circular
        v-if="isPriceLoading"
        indeterminate
        color="primary"
        :size="20"
        :width="2"
      />
      <!-- :disabled, not :readonly: buttons have no readonly state -->
      <v-btn
        v-else
        icon="mdi-delete-outline"
        size="small"
        variant="text"
        color="error"
        :disabled="props.readonly"
        @click="emit('delete', $event)"
      />
    </div>
  </fieldset>
</template>

<style module lang="scss">
@use '@/styles/grids';

.jobEditServiceFieldset {
  display: grid;
  border: none;
  margin: 0;
  grid-template-columns: grids.$servicesRowColumns;
  gap: 8px;
  align-items: start;
  padding: 8px 0 8px 8px;
}

.actions {
  display: flex;
  align-items: center;
  justify-content: center;
  align-self: stretch;
}
</style>
